/* ============================================================================
   PARHELIK — product controller (PDP / cards / quick-add)
   Vanilla, dependency-free, idempotent. Implements DESIGN-SYSTEM §8.

   Scoped per [data-ph-product]. For each product root it:
     • reads variants from <script type="application/json" data-ph-variants>
     • on [data-ph-option] / [data-ph-swatch] change -> resolves the selected
       variant from chosen option values, then updates:
         - hidden id input [data-ph-variant-id] (in [data-ph-product-form])
         - [data-ph-price] / [data-ph-price-compare] (hidden when no compare)
         - [data-ph-badge-sale] / [data-ph-badge-soldout]
         - [data-ph-atc] disabled state + [data-ph-atc-label] text
         - the URL (history.replaceState ?variant=id)
         - active gallery media ([data-ph-media]/[data-ph-thumb] .is-active)
     • .ph-qty steppers ([data-ph-qty-minus/plus] + [data-ph-qty-input], min 1)
     • sticky ATC ([data-ph-sticky-atc]) reveal via IntersectionObserver on the
       buy box; mirrors price + variant id; reuses the same add path
     • gallery: thumbnail click sets active media; optional prev/next
     • add-to-cart is delegated to window.PHCart (parhelik-cart.js); this file
       only guarantees the form carries the right variant id + quantity and
       calls window.PHCart.open() as a fallback.

   Public API: window.PHProduct.init(), .refresh(), .get(root)
   ========================================================================== */
(function () {
  'use strict';

  if (window.PHProduct && window.PHProduct._installed) return;

  /* ---- selectors (the §8 data-ph-* contract) ---- */
  var SEL = {
    root: '[data-ph-product]',
    variants: '[data-ph-variants]',
    form: '[data-ph-product-form]',
    variantId: '[data-ph-variant-id]',
    option: '[data-ph-option]',
    swatch: '[data-ph-swatch]',
    colorLabel: '[data-ph-color-label]',
    price: '[data-ph-price]',
    priceCompare: '[data-ph-price-compare]',
    badgeSale: '[data-ph-badge-sale]',
    badgeSoldout: '[data-ph-badge-soldout]',
    atc: '[data-ph-atc]',
    atcLabel: '[data-ph-atc-label]',
    qty: '.ph-qty',
    qtyInput: '[data-ph-qty-input]',
    qtyMinus: '[data-ph-qty-minus]',
    qtyPlus: '[data-ph-qty-plus]',
    gallery: '[data-ph-gallery]',
    media: '[data-ph-media]',
    thumb: '[data-ph-thumb]',
    galPrev: '[data-ph-gallery-prev]',
    galNext: '[data-ph-gallery-next]',
    buyBox: '[data-ph-buy-box]',
    sticky: '[data-ph-sticky-atc]',
    stickyPrice: '[data-ph-sticky-price]',
    stickyTitle: '[data-ph-sticky-title]',
    stickyVariantId: '[data-ph-sticky-variant-id]'
  };

  var INSTANCES = [];

  /* ---- tiny helpers ---- */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function moneyFormat() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
  }

  /* Format a price given in cents. Prefers a per-root template carried on the
     root ([data-ph-money-format], a Shopify money string with {{amount}}),
     falls back to a plain locale-grouped number. */
  function formatMoney(cents, root) {
    if (cents == null || isNaN(cents)) return '';
    var amount = (cents / 100);
    var tpl = root && root.getAttribute('data-ph-money-format');
    if (tpl && /\{\{\s*amount\s*\}\}/.test(tpl)) {
      var withDecimals = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      var noDecimals = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return tpl
        .replace(/\{\{\s*amount\s*\}\}/g, withDecimals)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'));
    }
    var grouped = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var sym = moneyFormat() ? ' ' + moneyFormat() : '';
    return '$' + grouped + sym;
  }

  function strings(root) {
    var data = (root && root.dataset) || {};
    return {
      addToCart: data.phStrAdd || 'Add to Bag',
      soldOut: data.phStrSoldout || 'Sold out',
      unavailable: data.phStrUnavailable || 'Unavailable'
    };
  }

  /* ============================================================
     Product instance
     ============================================================ */
  function Product(root) {
    this.root = root;
    this.variants = this._readVariants();
    this.strings = strings(root);
    this.currentVariant = null;
    this._io = null;
    this._init();
  }

  Product.prototype._readVariants = function () {
    var node = $(SEL.variants, this.root);
    if (!node) return [];
    try {
      var data = JSON.parse(node.textContent);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      console.warn('[PHProduct] bad variant JSON', e);
      return [];
    }
  };

  Product.prototype._init = function () {
    this.form = $(SEL.form, this.root);
    this.variantIdInput = $(SEL.variantId, this.root);

    this.currentVariant = this._resolveInitialVariant();

    this._bindOptions();
    this._bindQty();
    this._bindGallery();
    this._bindSticky();

    // Reflect the initial state (price/badges/atc/media) without touching URL.
    if (this.currentVariant) {
      this._applyVariant(this.currentVariant, { updateUrl: false });
    } else {
      this._setUnavailable();
    }

    // Gracefully handle media whose image fails to load (e.g. a corrupted /
    // non-READY CDN asset): drop the broken slide + thumb and, if it was the one
    // on screen, swap in the first working slide instead of a broken/empty box.
    this._handleBrokenMedia();
  };

  /* ---- which variant is selected at load ---- */
  Product.prototype._resolveInitialVariant = function () {
    if (!this.variants.length) return null;

    // 1) ?variant= in the URL wins.
    var params = new URLSearchParams(window.location.search);
    var urlId = params.get('variant');
    if (urlId) {
      var byUrl = this._variantById(urlId);
      if (byUrl) return byUrl;
    }

    // 2) the hidden input already carries an id (Liquid pre-selected one).
    if (this.variantIdInput && this.variantIdInput.value) {
      var byInput = this._variantById(this.variantIdInput.value);
      if (byInput) return byInput;
    }

    // 3) compute from whatever option controls are currently chosen.
    var fromOptions = this._variantFromSelectedOptions();
    if (fromOptions) return fromOptions;

    // 4) first available, else first.
    var firstAvail = this.variants.filter(function (v) { return v.available; })[0];
    return firstAvail || this.variants[0] || null;
  };

  Product.prototype._variantById = function (id) {
    id = String(id);
    for (var i = 0; i < this.variants.length; i++) {
      if (String(this.variants[i].id) === id) return this.variants[i];
    }
    return null;
  };

  /* ---- read currently chosen option values, in option-position order ---- */
  Product.prototype._selectedOptions = function () {
    var groups = $$(SEL.option, this.root);
    var swatches = $$(SEL.swatch, this.root);
    var values = [];

    function setAt(pos, val) {
      if (pos < 1 || val == null) return;
      values[pos - 1] = val;
    }

    // Grouped option controls (radios/selects wrapped in [data-ph-option]).
    groups.forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-option-position'), 10) || (values.length + 1);
      var val = null;

      var select = group.querySelector('select');
      if (select) {
        val = select.value;
      } else {
        var checked = group.querySelector('input:checked');
        if (checked) {
          val = checked.value;
        } else {
          // fall back to an element marked active.
          var active = group.querySelector('.is-active[value], [aria-checked="true"][value], [data-option-value].is-active');
          if (active) val = active.getAttribute('value') || active.getAttribute('data-option-value');
        }
      }
      setAt(pos, val);
    });

    // Standalone swatches (may or may not live inside a [data-ph-option]).
    swatches.forEach(function (sw) {
      if (sw.closest && sw.closest(SEL.option)) return; // already covered above
      var on = sw.classList.contains('is-active') || sw.getAttribute('aria-pressed') === 'true' || sw.getAttribute('aria-checked') === 'true';
      if (!on) return;
      var pos = parseInt(sw.getAttribute('data-option-position'), 10) || 1;
      var val = sw.getAttribute('data-option-value') || sw.getAttribute('value');
      setAt(pos, val);
    });

    return values;
  };

  Product.prototype._variantFromSelectedOptions = function () {
    var chosen = this._selectedOptions();
    if (!chosen.length) return null;

    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var opts = v.options || [];
      var match = true;
      for (var p = 0; p < chosen.length; p++) {
        if (chosen[p] == null) continue; // unselected position -> ignore
        if (String(opts[p]) !== String(chosen[p])) { match = false; break; }
      }
      if (match) return v;
    }
    return null;
  };

  /* ---- reflect a resolved variant back into the option controls ----
     Marks the matching swatch/pill/radio/select for each option position active
     and clears its siblings, so a variant reached via ?variant=, a thumbnail, or
     programmatically shows the right highlight (not a stale server value). */
  Product.prototype._syncOptionControls = function (variant) {
    var opts = (variant && variant.options) || [];
    $$(SEL.option, this.root).forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-option-position'), 10) || 1;
      var want = opts[pos - 1];
      if (want == null) return;
      $$('[data-option-value], [value]', group).forEach(function (c) {
        if (c.tagName === 'OPTION') return;
        var v = c.getAttribute('data-option-value');
        if (v == null) v = c.getAttribute('value');
        var on = String(v) === String(want);
        c.classList.toggle('is-active', on);
        if (c.hasAttribute('aria-pressed')) c.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (c.hasAttribute('aria-checked')) c.setAttribute('aria-checked', on ? 'true' : 'false');
        if (c.hasAttribute('aria-selected')) c.setAttribute('aria-selected', on ? 'true' : 'false');
        if (c.tagName === 'INPUT' && c.type === 'radio') c.checked = on;
      });
      var select = group.querySelector('select');
      if (select) select.value = want;
    });
  };

  /* Update the [data-ph-color-label] readouts (per-option value + the eyebrow). */
  Product.prototype._updateColorLabels = function (variant) {
    var opts = (variant && variant.options) || [];
    $$(SEL.option, this.root).forEach(function (group) {
      var pos = parseInt(group.getAttribute('data-option-position'), 10) || 1;
      var label = group.querySelector(SEL.colorLabel);
      if (label && opts[pos - 1] != null) label.textContent = opts[pos - 1];
    });
    // readouts outside any option group (e.g. the eyebrow) track option1
    $$(SEL.colorLabel, this.root).forEach(function (el) {
      if (el.closest(SEL.option)) return;
      if (opts[0] != null) el.textContent = opts[0];
    });
  };

  /* ---- option / swatch change handling ---- */
  Product.prototype._bindOptions = function () {
    var self = this;

    // change (radios, selects)
    this.root.addEventListener('change', function (e) {
      if (e.target.closest(SEL.option) || e.target.closest(SEL.swatch)) {
        self._onOptionChange();
      }
    });

    // click on swatches / pill buttons that aren't form inputs
    this.root.addEventListener('click', function (e) {
      var sw = e.target.closest(SEL.swatch);
      if (sw) {
        // toggle active state within the same option position
        var pos = sw.getAttribute('data-option-position') || '1';
        $$('[data-ph-swatch][data-option-position="' + pos + '"]', self.root).forEach(function (other) {
          other.classList.remove('is-active');
          other.setAttribute('aria-pressed', 'false');
        });
        // If no positions are declared, fall back to clearing all swatches.
        if (!sw.getAttribute('data-option-position')) {
          $$(SEL.swatch, self.root).forEach(function (other) {
            other.classList.remove('is-active');
            other.setAttribute('aria-pressed', 'false');
          });
        }
        sw.classList.add('is-active');
        sw.setAttribute('aria-pressed', 'true');
        self._onOptionChange();
        return;
      }

      // pill/button option values inside a [data-ph-option] that use buttons
      var optBtn = e.target.closest(SEL.option + ' [value], ' + SEL.option + ' [data-option-value]');
      if (optBtn && optBtn.tagName !== 'INPUT' && optBtn.tagName !== 'OPTION' && optBtn.tagName !== 'SELECT') {
        var group = optBtn.closest(SEL.option);
        if (group) {
          $$('[value], [data-option-value]', group).forEach(function (other) {
            other.classList.remove('is-active');
            other.setAttribute('aria-checked', 'false');
          });
          optBtn.classList.add('is-active');
          optBtn.setAttribute('aria-checked', 'true');
          self._onOptionChange();
        }
      }
    });
  };

  Product.prototype._onOptionChange = function () {
    var variant = this._variantFromSelectedOptions();
    this.currentVariant = variant; // may be null (combo not offered)
    if (!variant) {
      this._setUnavailable();
      return;
    }
    this._applyVariant(variant, { updateUrl: true });
  };

  /* ---- apply a resolved variant across the UI ---- */
  Product.prototype._applyVariant = function (variant, opts) {
    opts = opts || {};

    // hidden id input(s)
    this._setVariantInputs(variant.id);

    // reflect the selection in the option controls + the [data-ph-color-label] readouts
    this._syncOptionControls(variant);
    this._updateColorLabels(variant);

    // price + compare-at
    this._updatePrice(variant);

    // badges
    this._updateBadges(variant);

    // ATC enabled state + label
    this._updateAtc(variant);

    // URL
    if (opts.updateUrl !== false) {
      this._updateUrl(variant.id);
    }

    // gallery media
    this._setActiveMedia(variant);

    // mirror to sticky bar
    this._updateSticky(variant);

    // let other scripts react
    this.root.dispatchEvent(new CustomEvent('ph:variant:change', {
      bubbles: true,
      detail: { variant: variant, product: this }
    }));
  };

  Product.prototype._setVariantInputs = function (id) {
    var value = id == null ? '' : String(id);
    $$(SEL.variantId, this.root).forEach(function (input) {
      input.value = value;
    });
    $$(SEL.stickyVariantId, this.root).forEach(function (input) {
      input.value = value;
    });
  };

  Product.prototype._updatePrice = function (variant) {
    var root = this.root;
    var priceEl = $(SEL.price, this.root);
    if (priceEl) priceEl.textContent = formatMoney(variant.price, root);

    var compareEl = $(SEL.priceCompare, this.root);
    if (compareEl) {
      var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (hasCompare) {
        compareEl.textContent = formatMoney(variant.compare_at_price, root);
        compareEl.hidden = false;
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
        compareEl.style.display = 'none';
      }
    }
  };

  Product.prototype._updateBadges = function (variant) {
    var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    var soldOut = !variant.available;

    $$(SEL.badgeSale, this.root).forEach(function (el) {
      el.hidden = !onSale;
      el.style.display = onSale ? '' : 'none';
    });
    $$(SEL.badgeSoldout, this.root).forEach(function (el) {
      el.hidden = !soldOut;
      el.style.display = soldOut ? '' : 'none';
    });
  };

  Product.prototype._updateAtc = function (variant) {
    var label = variant.available ? this.strings.addToCart : this.strings.soldOut;
    this._setAtcState(!variant.available, label);
  };

  Product.prototype._setUnavailable = function () {
    this._setVariantInputs('');
    this._setAtcState(true, this.strings.unavailable);

    // hide sale/soldout badges + compare price when there's no valid variant
    var compareEl = $(SEL.priceCompare, this.root);
    if (compareEl) { compareEl.hidden = true; compareEl.style.display = 'none'; }
    $$(SEL.badgeSale, this.root).forEach(function (el) { el.hidden = true; el.style.display = 'none'; });
    $$(SEL.badgeSoldout, this.root).forEach(function (el) { el.hidden = true; el.style.display = 'none'; });
  };

  Product.prototype._setAtcState = function (disabled, label) {
    $$(SEL.atc, this.root).forEach(function (btn) {
      if (disabled) {
        btn.setAttribute('disabled', 'disabled');
      } else {
        btn.removeAttribute('disabled');
      }
      btn.classList.toggle('is-disabled', !!disabled);
      var labelEl = btn.querySelector(SEL.atcLabel) || (btn.matches(SEL.atcLabel) ? btn : null);
      if (labelEl && label != null) labelEl.textContent = label;
    });
    // standalone labels not nested in the button
    $$(SEL.atcLabel, this.root).forEach(function (el) {
      if (el.closest(SEL.atc)) return;
      if (label != null) el.textContent = label;
    });
  };

  Product.prototype._updateUrl = function (variantId) {
    if (this.root.getAttribute('data-ph-update-url') === 'false') return;
    if (!variantId) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    } catch (e) { /* non-fatal */ }
  };

  /* ---- gallery ---- */
  Product.prototype._bindGallery = function () {
    var self = this;
    var gallery = $(SEL.gallery, this.root);
    if (!gallery) return;

    this.root.addEventListener('click', function (e) {
      var thumb = e.target.closest(SEL.thumb);
      if (thumb) {
        e.preventDefault();
        self._activateMedia(thumb.getAttribute('data-media-id'), { scroll: true });
        return;
      }
      if (e.target.closest(SEL.galPrev)) { e.preventDefault(); self._stepMedia(-1); return; }
      if (e.target.closest(SEL.galNext)) { e.preventDefault(); self._stepMedia(1); return; }
    });

    self._initRailDrag(gallery.querySelector('.ph-gallery__rail'));
  };

  // Mouse drag-to-scroll for the thumbnail rail (desktop grab-pan). Touch already
  // scrolls the rail natively via overflow-x. A drag past a small threshold swallows
  // the trailing click so a drag never accidentally selects a thumb.
  Product.prototype._initRailDrag = function (rail) {
    if (!rail || rail._phDragBound) return;
    rail._phDragBound = true;
    var down = false, startX = 0, curX = 0, startScroll = 0, dragged = false;
    var THRESH = 6, vel = 0, raf = 0, mode = '';        // mode: '' | 'drag' | 'glide'
    var maxScroll = function () { return rail.scrollWidth - rail.clientWidth; };
    var stop = function () { if (raf) { cancelAnimationFrame(raf); raf = 0; } mode = ''; };

    // single rAF loop: applies the scroll ONCE PER FRAME (decoupled from the flood of
    // pointermove events) so the drag is smooth, then carries the flick with inertia.
    function loop() {
      var max = maxScroll(), n;
      if (mode === 'drag') {
        n = startScroll - (curX - startX);
        if (n < 0) n = 0; else if (n > max) n = max;
        vel = n - rail.scrollLeft;                       // px/frame → flick velocity
        rail.scrollLeft = n;
        raf = requestAnimationFrame(loop);
      } else if (mode === 'glide') {
        vel *= 0.93;
        n = rail.scrollLeft + vel;
        if (Math.abs(vel) < 0.2 || n <= 0 || n >= max) {
          rail.scrollLeft = n <= 0 ? 0 : (n >= max ? max : n);
          rail.style.scrollBehavior = ''; stop(); return;
        }
        rail.scrollLeft = n;
        raf = requestAnimationFrame(loop);
      }
    }

    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;              // touch scrolls natively (its own momentum)
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      stop();
      down = true; dragged = false; vel = 0;
      startX = curX = e.clientX; startScroll = rail.scrollLeft;
    });
    rail.addEventListener('pointermove', function (e) {
      if (!down) return;
      curX = e.clientX;                                   // just record; the rAF loop reads it
      if (!dragged) {
        if (Math.abs(curX - startX) < THRESH) return;
        dragged = true;
        rail.classList.add('is-dragging');
        rail.style.scrollBehavior = 'auto';               // set ONCE (not every move)
        try { rail.setPointerCapture(e.pointerId); } catch (err) {}  // ONCE
        mode = 'drag'; raf = requestAnimationFrame(loop);
      }
      e.preventDefault();
    });
    var end = function () {
      if (!down) return;
      down = false;
      rail.classList.remove('is-dragging');
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
      if (dragged) {
        var swallow = function (ev) { ev.stopPropagation(); ev.preventDefault(); };
        rail.addEventListener('click', swallow, true);
        setTimeout(function () { rail.removeEventListener('click', swallow, true); }, 0);
        if (Math.abs(vel) > 0.6) { mode = 'glide'; raf = requestAnimationFrame(loop); }   // flick → inertial glide
        else { mode = ''; rail.style.scrollBehavior = ''; }
      } else { mode = ''; rail.style.scrollBehavior = ''; }
    };
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // vertical mouse wheel → horizontal scroll (only when overflowing; leave trackpad deltaX native)
    rail.addEventListener('wheel', function (e) {
      var max = maxScroll();
      if (max <= 0 || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      stop();
      rail.style.scrollBehavior = 'auto';
      var n = Math.max(0, Math.min(max, rail.scrollLeft + e.deltaY));
      if (n !== rail.scrollLeft) { rail.scrollLeft = n; e.preventDefault(); }
    }, { passive: false });
  };

  Product.prototype._mediaList = function () {
    return $$(SEL.media, this.root);
  };

  Product.prototype._activeMediaIndex = function () {
    var medias = this._mediaList();
    for (var i = 0; i < medias.length; i++) {
      if (medias[i].classList.contains('is-active')) return i;
    }
    return 0;
  };

  Product.prototype._stepMedia = function (dir) {
    var medias = this._mediaList();
    if (!medias.length) return;
    var i = (this._activeMediaIndex() + dir + medias.length) % medias.length;
    var id = medias[i].getAttribute('data-media-id');
    this._activateMedia(id, { scroll: true });
  };

  Product.prototype._activateMedia = function (mediaId, opts) {
    if (mediaId == null) return;
    opts = opts || {};
    var target = String(mediaId);
    var activeThumb = null;

    $$(SEL.media, this.root).forEach(function (m) {
      var on = String(m.getAttribute('data-media-id')) === target;
      m.classList.toggle('is-active', on);
    });
    $$(SEL.thumb, this.root).forEach(function (t) {
      var on = String(t.getAttribute('data-media-id')) === target;
      t.classList.toggle('is-active', on);
      if (on) { t.setAttribute('aria-current', 'true'); activeThumb = t; }
      else t.removeAttribute('aria-current');
    });

    // Scroll the active THUMB into the rail (horizontal), NOT the stacked slide —
    // the slides share grid-area 1/1, so scrolling a slide does nothing for the
    // overflowing thumbnail rail and can yank the sticky media panel vertically.
    // Center the active thumb in the rail by setting scrollLeft directly (the rail's CSS
    // scroll-behavior:smooth animates it). scrollIntoView is avoided: inside the thumb-click
    // handler it intermittently no-ops (scroll-snap conflict) and block:'nearest' can yank
    // the sticky page vertically.
    if (activeThumb && opts.scroll) {
      var rail = activeThumb.closest('.ph-gallery__rail');
      if (rail && rail.scrollWidth > rail.clientWidth + 4) {
        var tr = activeThumb.getBoundingClientRect();
        var rr = rail.getBoundingClientRect();
        var max = rail.scrollWidth - rail.clientWidth;
        var dest = Math.max(0, Math.min(max, rail.scrollLeft + (tr.left - rr.left) - (rr.width - tr.width) / 2));
        // instant (forced) — smooth scrolling no-ops on this rail in Chrome; instant is reliable
        var sb = rail.style.scrollBehavior;
        rail.style.scrollBehavior = 'auto';
        rail.scrollLeft = dest;
        rail.style.scrollBehavior = sb;
      }
    }
  };

  Product.prototype._anyVariantHasMedia = function () {
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i] && this.variants[i].featured_media) return true;
    }
    return false;
  };

  Product.prototype._setActiveMedia = function (variant) {
    var medias = this._mediaList();
    if (!medias.length) return;

    var mediaId = (variant && variant.featured_media)
      ? (variant.featured_media.id != null ? variant.featured_media.id : variant.featured_media)
      : null;

    // Does the variant's featured media exist as a slide in THIS gallery?
    // (iterate + compare — avoids building an unescaped attribute selector.)
    var target = null;
    if (mediaId != null) {
      var idStr = String(mediaId);
      for (var i = 0; i < medias.length; i++) {
        if (String(medias[i].getAttribute('data-media-id')) === idStr) { target = idStr; break; }
      }
    }

    // No media for this variant: if the product uses per-variant media at all
    // (e.g. the toothbrush colours plus a media-less refill), reset to the first
    // slide so we don't strand the previous variant's photo. If NO variant has
    // media (e.g. the bidet), leave whatever the shopper is viewing.
    if (target == null) {
      if (this._anyVariantHasMedia()) {
        target = medias[0].getAttribute('data-media-id');
      } else {
        return;
      }
    }

    this._activateMedia(target, { scroll: false });
  };

  /* ---- broken-media resilience ---- */
  Product.prototype._handleBrokenMedia = function () {
    var self = this;
    $$(SEL.media + ' img', this.root).forEach(function (img) {
      var handled = false;
      function onBad() {
        if (handled) return;
        handled = true;
        var slide = img.closest(SEL.media);
        if (!slide) return;
        slide.setAttribute('data-ph-broken', '');
        var mid = String(slide.getAttribute('data-media-id'));
        // hide the matching thumbnail
        $$(SEL.thumb, self.root).forEach(function (t) {
          if (String(t.getAttribute('data-media-id')) === mid) t.style.display = 'none';
        });
        // if the broken media is the one on screen, switch to the first good slide
        if (slide.classList.contains('is-active')) {
          var good = self._mediaList().filter(function (m) { return !m.hasAttribute('data-ph-broken'); })[0];
          if (good) self._activateMedia(good.getAttribute('data-media-id'), { scroll: false });
        }
      }
      // already failed before our listener attached? (eager image that errored)
      if (img.complete && img.naturalWidth === 0 && (img.currentSrc || img.src)) onBad();
      img.addEventListener('error', onBad);
    });
  };

  /* ---- quantity steppers (min 1) ---- */
  Product.prototype._bindQty = function () {
    var self = this;
    this.root.addEventListener('click', function (e) {
      var minus = e.target.closest(SEL.qtyMinus);
      if (minus) {
        e.preventDefault();
        self._stepQty(minus, -1);
        return;
      }
      var plus = e.target.closest(SEL.qtyPlus);
      if (plus) {
        e.preventDefault();
        self._stepQty(plus, 1);
        return;
      }
    });

    this.root.addEventListener('change', function (e) {
      var input = e.target.closest(SEL.qtyInput);
      if (input) self._clampQty(input);
    });
  };

  Product.prototype._stepQty = function (btn, dir) {
    var wrap = btn.closest(SEL.qty) || this.root;
    var input = $(SEL.qtyInput, wrap) || $(SEL.qtyInput, this.root);
    if (!input) return;
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = 1;
    var next = Math.max(1, current + dir);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  Product.prototype._clampQty = function (input) {
    var v = parseInt(input.value, 10);
    if (isNaN(v) || v < 1) v = 1;
    input.value = v;
    return v;
  };

  Product.prototype._currentQty = function () {
    var input = $(SEL.qtyInput, this.root);
    if (!input) return 1;
    return this._clampQty(input);
  };

  /* ---- sticky add-to-cart ---- */
  Product.prototype._bindSticky = function () {
    var sticky = $(SEL.sticky, this.root);
    var buyBox = $(SEL.buyBox, this.root) || (this.form ? this.form.closest('[data-ph-buy-box]') || this.form : null);
    if (!sticky || !buyBox || typeof IntersectionObserver === 'undefined') return;

    this._io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // reveal the sticky bar once the buy box has scrolled out of view
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { rootMargin: '0px 0px -40% 0px', threshold: 0 });

    this._io.observe(buyBox);
  };

  Product.prototype._updateSticky = function (variant) {
    var priceEl = $(SEL.stickyPrice, this.root);
    if (priceEl) priceEl.textContent = formatMoney(variant.price, this.root);

    var titleEl = $(SEL.stickyTitle, this.root);
    if (titleEl && variant.title) titleEl.textContent = variant.title;
  };

  Product.prototype.destroy = function () {
    if (this._io) { this._io.disconnect(); this._io = null; }
  };

  /* ============================================================
     Add-to-cart fallback.
     parhelik-cart.js already intercepts [data-ph-add-to-cart] form submit
     -> POST /cart/add.js -> opens the drawer. We do NOT duplicate that. We
     only make sure the form carries the right variant id + qty before submit,
     and (defensively) call PHCart.open() as a fallback if the cart script is
     present but a submit slipped through without opening.
     ============================================================ */
  function syncFormBeforeSubmit(form) {
    var root = form.closest(SEL.root);
    if (!root) return;
    var inst = getInstance(root);
    if (!inst || !inst.currentVariant) return;

    // ensure id
    var idInput = form.querySelector('input[name="id"]') || form.querySelector(SEL.variantId);
    if (idInput && (!idInput.value || idInput.value === '')) {
      idInput.value = inst.currentVariant.id;
    }
    // ensure quantity mirrors the visible stepper (when the form lacks its own)
    var qtyField = form.querySelector('input[name="quantity"]');
    if (qtyField && (!qtyField.value || parseInt(qtyField.value, 10) < 1)) {
      qtyField.value = inst._currentQty();
    }
  }

  function onSubmitCapture(e) {
    var form = e.target.closest && e.target.closest('[data-ph-add-to-cart]');
    if (!form) return;
    // Sync the variant id/qty BEFORE PHCart's own (bubbling) handler reads FormData.
    syncFormBeforeSubmit(form);

    // Fallback: if PHCart isn't installed, do nothing here (let the form post
    // normally to /cart). If it is installed it will intercept on bubble phase.
    if (!(window.PHCart && window.PHCart._installed)) {
      // allow native navigation to /cart — but still try to open afterward.
      return;
    }
  }

  // Sticky/compact ATC buttons that aren't <button type=submit> inside the form:
  // forward their click to the real product form submit so PHCart handles it.
  function onClickForward(e) {
    var trigger = e.target.closest && e.target.closest('[data-ph-sticky-atc] [data-ph-atc], [data-ph-atc][data-ph-forward]');
    if (!trigger) return;
    if (trigger.hasAttribute('disabled')) { e.preventDefault(); return; }
    var root = trigger.closest(SEL.root);
    if (!root) return;
    var inst = getInstance(root);
    var form = inst && inst.form;
    // If this button is already a submit button inside the form, let it be.
    if (form && form.contains(trigger) && (trigger.type === 'submit')) return;

    if (form) {
      e.preventDefault();
      syncFormBeforeSubmit(form);
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    } else if (window.PHCart && inst && inst.currentVariant) {
      // no form: add by id then open the drawer
      e.preventDefault();
      window.PHCart.addId(inst.currentVariant.id, inst._currentQty());
    }
  }

  /* ============================================================
     Registry / lifecycle
     ============================================================ */
  function getInstance(root) {
    for (var i = 0; i < INSTANCES.length; i++) {
      if (INSTANCES[i].root === root) return INSTANCES[i];
    }
    return null;
  }

  function mount(root) {
    if (!root || root.__phProduct) return getInstance(root);
    var inst = new Product(root);
    root.__phProduct = true;
    INSTANCES.push(inst);
    return inst;
  }

  function unmount(root) {
    for (var i = INSTANCES.length - 1; i >= 0; i--) {
      if (INSTANCES[i].root === root) {
        try { INSTANCES[i].destroy(); } catch (e) { /* noop */ }
        INSTANCES.splice(i, 1);
      }
    }
    if (root) root.__phProduct = false;
  }

  function init(scope) {
    var ctx = scope || document;
    // if scope itself is a product root, mount it too
    if (ctx.matches && ctx.matches(SEL.root)) mount(ctx);
    $$(SEL.root, ctx).forEach(mount);
  }

  function refresh() {
    // drop instances whose roots left the DOM, then (re)mount everything present
    for (var i = INSTANCES.length - 1; i >= 0; i--) {
      if (!document.contains(INSTANCES[i].root)) {
        try { INSTANCES[i].destroy(); } catch (e) { /* noop */ }
        INSTANCES.splice(i, 1);
      }
    }
    init(document);
  }

  function bindGlobal() {
    // capture phase so we sync the variant id BEFORE PHCart reads FormData on bubble
    document.addEventListener('submit', onSubmitCapture, true);
    document.addEventListener('click', onClickForward);
  }

  /* ---- public API ---- */
  window.PHProduct = {
    _installed: true,
    init: init,
    refresh: refresh,
    get: getInstance,
    mount: mount,
    unmount: unmount,
    instances: INSTANCES
  };

  function boot() {
    bindGlobal();
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init the loaded/reloaded section's products.
  document.addEventListener('shopify:section:load', function (e) {
    var target = (e && e.target) || document;
    // re-mount any product roots inside the (re)loaded section
    $$(SEL.root, target).forEach(function (root) {
      unmount(root);
      mount(root);
    });
    if (target.matches && target.matches(SEL.root)) { unmount(target); mount(target); }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var target = (e && e.target) || document;
    $$(SEL.root, target).forEach(unmount);
  });
})();
